import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestActivateProgramme,
  requestCreateProgramme,
  requestUpdateProgramme,
  requestViewAllProgrammes,
  requestViewProgramme,
  requestViewUnassignedMembers,
  requestDownloadTeamLeaderboardExcel,
  requestSendMemberActivationCommunications,
  requestUploadMemberExcel
} from './programme.actions';
import { requestViewAllMembers } from '../member/member.actions';
import {
  getActivateProgrammeRequest,
  getCreateProgrammeRequest,
  getUpdateProgrammeRequest,
  getViewAllProgrammesRequest,
  getViewProgrammeRequest,
  getViewUnassignedMembersRequest,
  getDownloadTeamLeaderboardExcelRequest,
  getSendMemberActivationCommunicationsRequest,
  getUploadMemberExcelRequest
} from './programme.endpoints';
import {
  CREATING_PROGRAMME,
  RETRIEVE_ALL_PROGRAMMES,
  RETRIEVE_PROGRAMME,
  UPDATING_PROGRAMME,
  PUBLISHING_PROGRAMME,
  RETRIEVING_UNASSIGNED_MEMBERS,
  DOWNLOADING_TEAM_LEADERBOARD_EXCEL,
  SENDING_MEMBER_COMMUNICATIONS,
  UPLOADING_MEMBER_EXCEL
} from './programme.constants';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setProgramme, setProgrammes } from '../../slices/programme/programme.slice';
import { setUnassignedMembers } from '../../slices/programme/member/member.slice';

import { SUCCESS, ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { downloadExcelFile } from '../../utils/util';

export function* performCreateProgrammeRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_PROGRAMME));

    const [endpoint, options] = getCreateProgrammeRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.programmes[0].externalReference);
    }

    yield put(removeSpinner(CREATING_PROGRAMME));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllProgrammes({}));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(CREATING_PROGRAMME));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreateProgrammeRequest() {
  yield takeLatest(requestCreateProgramme.type, performCreateProgrammeRequest);
}

export function* performViewAllProgrammesRequest({ payload }) {
  const { onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVE_ALL_PROGRAMMES));

    const [endpoint, options] = getViewAllProgrammesRequest();

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setProgrammes(data.programmes));
    yield put(removeSpinner(RETRIEVE_ALL_PROGRAMMES));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(RETRIEVE_ALL_PROGRAMMES));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewAllProgrammesRequest() {
  yield takeLatest(requestViewAllProgrammes.type, performViewAllProgrammesRequest);
}

export function* performViewProgrammeRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVE_PROGRAMME));

    const [endpoint, options] = getViewProgrammeRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setProgramme(data.programmes[0]));
    yield put(removeSpinner(RETRIEVE_PROGRAMME));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(RETRIEVE_PROGRAMME));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewProgrammeRequest() {
  yield takeLatest(requestViewProgramme.type, performViewProgrammeRequest);
}

export function* performUpdateProgrammeRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_PROGRAMME));

    const [endpoint, options] = getUpdateProgrammeRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data.programmes[0].externalReference);
    }

    yield put(removeSpinner(UPDATING_PROGRAMME));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllProgrammes({}));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(UPDATING_PROGRAMME));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdateProgrammeRequest() {
  yield takeLatest(requestUpdateProgramme.type, performUpdateProgrammeRequest);
}

export function* performActivateProgrammeRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner(PUBLISHING_PROGRAMME));

    const [endpoint, options] = getActivateProgrammeRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(requestViewAllProgrammes({}));
    yield put(removeSpinner(PUBLISHING_PROGRAMME));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(PUBLISHING_PROGRAMME));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForActivateProgrammeRequest() {
  yield takeLatest(requestActivateProgramme.type, performActivateProgrammeRequest);
}

export function* performViewUnassignedMembersRequest({ payload }) {
  const { externalReference } = payload;

  try {
    yield put(addSpinner(RETRIEVING_UNASSIGNED_MEMBERS));

    const request = getViewUnassignedMembersRequest(externalReference);

    const { data } = yield call(axios, ...request);

    yield put(setUnassignedMembers(data.unassignedMembers));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_UNASSIGNED_MEMBERS));
  }
}

export function* watchForViewUnassignedMembersRequest() {
  yield takeLatest(requestViewUnassignedMembers.type, performViewUnassignedMembersRequest);
}

export function* performDownloadTeamLeaderboardExcel({ payload }) {
  const { input } = payload;

  try {
    yield put(addSpinner(DOWNLOADING_TEAM_LEADERBOARD_EXCEL));

    const request = getDownloadTeamLeaderboardExcelRequest(input);

    const { data } = yield call(axios, ...request);

    yield call(downloadExcelFile, data.base64EncodedString, `${input.programmeName} - Team Leaderboard.xlsx`);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(DOWNLOADING_TEAM_LEADERBOARD_EXCEL));
  }
}

export function* watchForDownloadTeamLeaderboardExcel() {
  yield takeLatest(requestDownloadTeamLeaderboardExcel.type, performDownloadTeamLeaderboardExcel);
}

export function* performSendMemberActivationCommunicationsRequest({ payload }) {
  const { programmeExternalReference, memberExternalReference, sendSms, sendEmail } = payload;

  try {
    yield put(addSpinner(SENDING_MEMBER_COMMUNICATIONS));

    const request = getSendMemberActivationCommunicationsRequest(
      programmeExternalReference,
      memberExternalReference,
      sendSms,
      sendEmail
    );

    const { data } = yield call(axios, ...request);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(SENDING_MEMBER_COMMUNICATIONS));
  }
}

export function* watchForSendMemberActivationCommunicationsRequest() {
  yield takeLatest(requestSendMemberActivationCommunications.type, performSendMemberActivationCommunicationsRequest);
}

export function* performUploadMemberExcelRequest({ payload }) {
  const { programmeExternalReference, base64String, onSuccess } = payload;

  try {
    yield put(addSpinner(UPLOADING_MEMBER_EXCEL));

    const [endpoint, options] = getUploadMemberExcelRequest(programmeExternalReference, base64String);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner(UPLOADING_MEMBER_EXCEL));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(requestViewAllMembers({ externalReference: programmeExternalReference }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(UPLOADING_MEMBER_EXCEL));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUploadMemberExcelRequest() {
  yield takeLatest(requestUploadMemberExcel.type, performUploadMemberExcelRequest);
}

export default function* programmeSaga() {
  yield all([
    watchForCreateProgrammeRequest(),
    watchForViewAllProgrammesRequest(),
    watchForViewProgrammeRequest(),
    watchForUpdateProgrammeRequest(),
    watchForActivateProgrammeRequest(),
    watchForViewUnassignedMembersRequest(),
    watchForDownloadTeamLeaderboardExcel(),
    watchForSendMemberActivationCommunicationsRequest(),
    watchForUploadMemberExcelRequest()
  ]);
}
